import { Link } from 'react-router-dom';
import { useEffect, useState, FormEvent, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { tryApiRequest, apiRequest, getAuthUser, uploadFiles, getApiCacheSync, invalidateApiCache, API_BASE, type BackendPost } from '../lib/api';
import ConfirmModal from '../components/ConfirmModal';
import ReactQuill, { Quill } from 'react-quill';
import EditablePageHeader from '../components/EditablePageHeader';
import 'react-quill/dist/quill.snow.css';

export const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];

export const QUILL_COLORS = [
  false,
  // Grayscale & Neutrals
  '#000000', '#333333', '#555555', '#777777', '#999999', '#cccccc', '#ffffff',
  // Reds & Pinks
  '#991b1b', '#dc2626', '#ef4444', '#f87171', '#f43f5e', '#fb7185', '#fda4af',
  // Oranges & Ambers
  '#9a3412', '#c2410c', '#ea580c', '#f97316', '#fb923c', '#d97706', '#f59e0b',
  // Yellows & Limes
  '#b45309', '#eab308', '#facc15', '#fde047', '#65a30d', '#84cc16', '#a3e635',
  // Greens & Teals
  '#166534', '#15803d', '#16a34a', '#22c55e', '#4ade80', '#0d9488', '#14b8a6',
  // Blues & Cyans
  '#0e7490', '#0284c7', '#0ea5e9', '#38bdf8', '#1d4ed8', '#2563eb', '#3b82f6',
  // Purples & Violets
  '#581c87', '#6b21a8', '#7e22ce', '#9333ea', '#a855f7', '#c084fc', '#e879f9'
];

let quillFormatsRegistered = false;
function registerQuillFormats() {
  if (quillFormatsRegistered) return;
  quillFormatsRegistered = true;
  
  const BaseImageFormat = Quill.import('formats/image');
  if (BaseImageFormat) {
    const ImageFormatAttributesList = ['alt', 'height', 'width', 'style', 'data-align'];
    class CustomImage extends BaseImageFormat {
      static create(value: any) {
        const src = typeof value === 'object' && value ? value.src : value;
        const node = super.create(src);
        if (typeof value === 'object' && value) {
          if (value.width) {
            node.setAttribute('width', String(value.width));
            const w = String(value.width);
            node.style.width = w.endsWith('%') || w.endsWith('px') ? w : `${w}px`;
          }
          if (value.height) {
            node.setAttribute('height', String(value.height));
            const h = String(value.height);
            node.style.height = h.endsWith('%') || h.endsWith('px') ? h : `${h}px`;
          }
          if (value.style) node.setAttribute('style', value.style);
          if (value.alt) node.setAttribute('alt', value.alt);
          if (value['data-align']) node.setAttribute('data-align', value['data-align']);
        }
        return node;
      }

      static formats(domNode: Element) {
        const formats: Record<string, string> = {};
        ImageFormatAttributesList.forEach((attribute) => {
          if (domNode.hasAttribute(attribute)) {
            formats[attribute] = domNode.getAttribute(attribute)!;
          }
        });
        if (!formats.width && (domNode as HTMLElement).style?.width) {
          formats.width = (domNode as HTMLElement).style.width.replace('px', '');
        }
        if (!formats.height && (domNode as HTMLElement).style?.height) {
          formats.height = (domNode as HTMLElement).style.height.replace('px', '');
        }
        return formats;
      }
      
      format(name: string, value: string) {
        if (ImageFormatAttributesList.indexOf(name) > -1) {
          if (value) {
            this.domNode.setAttribute(name, value);
            if (name === 'width') {
              const val = String(value);
              this.domNode.style.width = val.endsWith('%') || val.endsWith('px') ? val : `${val}px`;
            } else if (name === 'height') {
              const val = String(value);
              this.domNode.style.height = val.endsWith('%') || val.endsWith('px') ? val : `${val}px`;
            } else if (name === 'style') {
              this.domNode.setAttribute('style', value);
            }
          } else {
            this.domNode.removeAttribute(name);
            if (name === 'width') this.domNode.style.width = '';
            if (name === 'height') this.domNode.style.height = '';
          }
        } else {
          super.format(name, value);
        }
      }
    }
    Quill.register(CustomImage, true);
  }

  const BaseVideoFormat = Quill.import('formats/video');
  if (BaseVideoFormat) {
    class CustomVideo extends BaseVideoFormat {
      static create(value: string) {
        let url = value;
        if (url.includes('youtube.com') || url.includes('youtu.be')) {
          if (!url.includes('rel=')) {
            url += (url.includes('?') ? '&' : '?') + 'rel=0&modestbranding=1';
          }
        }
        const node = super.create(url);
        node.setAttribute('src', url);
        node.setAttribute('frameborder', '0');
        node.setAttribute('allowfullscreen', 'true');
        node.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
        return node;
      }
    }
    Quill.register(CustomVideo, true);
  }

  // Register inline style attributors so color & background save as inline CSS styles
  try {
    const ColorStyle = Quill.import('attributors/style/color');
    if (ColorStyle) Quill.register(ColorStyle, true);
    const BackgroundStyle = Quill.import('attributors/style/background');
    if (BackgroundStyle) Quill.register(BackgroundStyle, true);
    const AlignStyle = Quill.import('attributors/style/align');
    if (AlignStyle) Quill.register(AlignStyle, true);
    const SizeStyle = Quill.import('attributors/style/size');
    if (SizeStyle) {
      SizeStyle.whitelist = FONT_SIZES;
      Quill.register(SizeStyle, true);
    }

    const BaseClipboard = Quill.import('modules/clipboard');
    if (BaseClipboard) {
      class CustomClipboard extends BaseClipboard {
        constructor(quill: any, options: any) {
          super(quill, options);
          this.addMatcher('IMG', (node: HTMLElement, delta: any) => {
            const src = node.getAttribute('src') || '';
            const attributes: Record<string, any> = {};
            const width = node.getAttribute('width') || node.style?.width;
            const height = node.getAttribute('height') || node.style?.height;
            const style = node.getAttribute('style');
            const alt = node.getAttribute('alt');
            const align = node.getAttribute('data-align');

            if (width) attributes.width = width.replace('px', '');
            if (height) attributes.height = height.replace('px', '');
            if (style) attributes.style = style;
            if (alt) attributes.alt = alt;
            if (align) attributes['data-align'] = align;

            const DeltaClass = delta ? delta.constructor : Quill.import('delta');
            return new DeltaClass().insert({ image: src }, attributes);
          });
        }

        convert(html?: any) {
          const delta = super.convert(html);
          if (delta && Array.isArray(delta.ops)) {
            const newOps: any[] = [];
            for (let i = 0; i < delta.ops.length; i++) {
              const op = delta.ops[i];
              const nextOp = delta.ops[i + 1];

              // If next op is a list item or heading:
              if (nextOp && (nextOp.attributes?.list || nextOp.attributes?.header)) {
                // 1. If current op ends with multiple newlines, collapse to a single \n
                if (typeof op.insert === 'string' && op.insert.endsWith('\n\n')) {
                  op.insert = op.insert.replace(/\n+$/, '\n');
                }
                // 2. If current op is purely a blank newline and previous op already ended with \n
                if (op.insert === '\n' && newOps.length > 0) {
                  const prev = newOps[newOps.length - 1];
                  if (typeof prev.insert === 'string' && prev.insert.endsWith('\n')) {
                    continue;
                  }
                }
              }
              newOps.push(op);
            }
            delta.ops = newOps;
          }
          return delta;
        }
      }
      Quill.register('modules/clipboard', CustomClipboard, true);
    }
  } catch (e) {
    console.error('Error registering Quill style attributors:', e);
  }

  Quill.register('modules/imageFormatter', ImageFormatter);
}

class ImageFormatter {
  private quill: any;
  private overlay: HTMLDivElement | null = null;
  private selectedImage: HTMLImageElement | null = null;

  constructor(quill: any) {
    this.quill = quill;
    quill.root.addEventListener('click', (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof HTMLImageElement) {
        this.select(target);
      } else if (this.overlay && !(target instanceof HTMLElement && this.overlay.contains(target))) {
        this.clear();
      }
    });
    window.addEventListener('resize', () => this.positionOverlay());
  }

  private select(image: HTMLImageElement) {
    this.selectedImage = image;
    if (!this.overlay) {
      this.overlay = document.createElement('div');
      this.overlay.className = 'image-formatter-overlay';
      ['top-left', 'top-right', 'bottom-left', 'bottom-right'].forEach((position) => {
        const handle = document.createElement('button');
        handle.type = 'button';
        handle.className = `image-formatter-handle ${position}`;
        handle.addEventListener('mousedown', (event) => this.startResize(event, position));
        this.overlay?.appendChild(handle);
      });
      const toolbar = document.createElement('div');
      toolbar.className = 'image-formatter-toolbar';
      (['left', 'center', 'right'] as const).forEach((align) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = align === 'left' ? 'Trái' : align === 'center' ? 'Giữa' : 'Phải';
        button.addEventListener('click', () => this.align(align));
        toolbar.appendChild(button);
      });
      this.overlay.appendChild(toolbar);
      this.quill.root.parentNode.appendChild(this.overlay);
    }
    this.positionOverlay();
  }

  private positionOverlay() {
    if (!this.overlay || !this.selectedImage) return;
    const imageRect = this.selectedImage.getBoundingClientRect();
    const parentRect = this.quill.root.parentNode.getBoundingClientRect();
    Object.assign(this.overlay.style, {
      display: 'block',
      left: `${imageRect.left - parentRect.left}px`,
      top: `${imageRect.top - parentRect.top}px`,
      width: `${imageRect.width}px`,
      height: `${imageRect.height}px`,
    });
  }

  private startResize(event: MouseEvent, position: string) {
    event.preventDefault();
    if (!this.selectedImage) return;
    const startX = event.clientX;
    const startWidth = this.selectedImage.getBoundingClientRect().width;
    const ratio = (this.selectedImage.naturalHeight || this.selectedImage.height || 1) / (this.selectedImage.naturalWidth || this.selectedImage.width || 1);
    const direction = position.includes('left') ? -1 : 1;
    const onMove = (moveEvent: MouseEvent) => {
      const width = Math.max(80, Math.round(startWidth + (moveEvent.clientX - startX) * direction));
      const height = Math.round(width * ratio);
      this.selectedImage!.setAttribute('width', String(width));
      this.selectedImage!.setAttribute('height', String(height));
      this.selectedImage!.style.width = `${width}px`;
      this.selectedImage!.style.height = `${height}px`;
      this.positionOverlay();
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (this.selectedImage) {
        const blot = Quill.find(this.selectedImage) as any;
        if (blot && typeof blot.format === 'function') {
          blot.format('width', this.selectedImage.getAttribute('width'));
          blot.format('height', this.selectedImage.getAttribute('height'));
          blot.format('style', this.selectedImage.getAttribute('style') || '');
        }
        this.quill.update('user');
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  private align(align: 'left' | 'center' | 'right') {
    if (!this.selectedImage) return;
    this.selectedImage.style.display = 'block';
    this.selectedImage.style.marginLeft = align === 'right' ? 'auto' : align === 'center' ? 'auto' : '0';
    this.selectedImage.style.marginRight = align === 'left' ? 'auto' : align === 'center' ? 'auto' : '0';
    this.selectedImage.setAttribute('data-align', align);
    this.positionOverlay();
    const blot = Quill.find(this.selectedImage) as any;
    if (blot && typeof blot.format === 'function') {
      blot.format('style', this.selectedImage.getAttribute('style') || '');
      blot.format('data-align', align);
    }
    this.quill.update('user');
  }

  private clear() {
    if (this.overlay) this.overlay.style.display = 'none';
    this.selectedImage = null;
  }
}

const emptyPost = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  category: 'Hướng dẫn',
  coverImage: '',
  publishedAt: '',
};

function makeSlug(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function normalizeEditorContent(html: string) {
  if (!html) return '';
  // 1. Remove whitespace and newlines between block elements so Quill does not turn them into empty paragraphs
  let cleaned = html.replace(/(<\/(?:p|div|h[1-6]|ul|ol|li|blockquote|table|tr|td|th)>)\s+(<(?:p|div|h[1-6]|ul|ol|li|blockquote|table|tr|td|th)[^>]*>)/gi, '$1$2');

  const documentFragment = new DOMParser().parseFromString(cleaned, 'text/html');
  documentFragment.body.querySelectorAll('div').forEach((element) => {
    if (!element.querySelector('p, div, img, iframe, video, table')) {
      const paragraph = document.createElement('p');
      paragraph.innerHTML = element.innerHTML.replace(/\r?\n/g, '<br>');
      element.replaceWith(paragraph);
    }
  });

  documentFragment.body.querySelectorAll('p, div').forEach((block) => {
    const next = block.nextElementSibling;
    const prev = block.previousElementSibling;
    const isAdjacentToListOrHeading = 
      (next && ['UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TABLE', 'BLOCKQUOTE'].includes(next.tagName)) ||
      (prev && ['UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TABLE', 'BLOCKQUOTE'].includes(prev.tagName));
    const hasMedia = block.querySelector('img, iframe, video, table, a');
    const text = block.textContent?.replace(/\u00a0/g, ' ').trim() || '';

    // Remove empty paragraphs that only serve as meaningless spacers next to lists, headings, or tables
    if (!hasMedia && !text && isAdjacentToListOrHeading) {
      block.remove();
      return;
    }
    if (!hasMedia && !text && !block.querySelector('br')) {
      block.remove();
    }
  });

  return documentFragment.body.innerHTML
    // Remove consecutive empty paragraphs
    .replace(/(<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>|&nbsp;|\s)*\s*<\/p>\s*){2,}/gi, '<p><br></p>')
    // Remove empty paragraphs before headers, lists, tables, blockquotes
    .replace(/<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>|&nbsp;|\s)*\s*<\/p>\s*(<(?:h[1-6]|ul|ol|table|blockquote))/gi, '$1')
    // Remove empty paragraphs after headers, lists, tables, blockquotes
    .replace(/(<\/(?:h[1-6]|ul|ol|table|blockquote)>)\s*<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>|&nbsp;|\s)*\s*<\/p>/gi, '$1')
    // Remove leading and trailing empty paragraphs
    .replace(/^\s*(<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>|&nbsp;|\s)*\s*<\/p>\s*)+/gi, '')
    .replace(/(<p(?:\s[^>]*)?>\s*(?:<br\s*\/?>|&nbsp;|\s)*\s*<\/p>\s*)+$/gi, '')
    // Remove any remaining whitespace between block tags
    .replace(/(<\/(?:p|div|h[1-6]|ul|ol|li|blockquote|table)>)\s+(<(?:p|div|h[1-6]|ul|ol|li|blockquote|table)[^>]*>)/gi, '$1$2')
    .trim();
}

export function isArticlePost(p: BackendPost | null | undefined): boolean {
  if (!p || !p.slug) return false;
  if (p.category === 'System' || p.category === 'Trang') return false;
  const systemSlugs = ['about-page', 'site-meta', 'site-footer', 'home-layout'];
  if (systemSlugs.includes(p.slug)) return false;
  if (p.slug.startsWith('site-') || p.slug.startsWith('home-')) return false;
  return true;
}

async function processBase64Images(htmlContent: string, onProgress?: (msg: string) => void): Promise<string> {
  const base64Regex = /<img[^>]+src=["'](data:image\/([a-zA-Z0-9.+]+);base64,([^"']+))["'][^>]*>/g;
  let match;
  const toReplace: { dataUrl: string; mimeType: string; base64Data: string }[] = [];

  while ((match = base64Regex.exec(htmlContent)) !== null) {
    toReplace.push({
      dataUrl: match[1],
      mimeType: match[2],
      base64Data: match[3],
    });
  }

  if (toReplace.length === 0) return htmlContent;

  onProgress?.(`Đang tự động tải ${toReplace.length} ảnh lên máy chủ...`);
  let updatedContent = htmlContent;

  for (let i = 0; i < toReplace.length; i++) {
    const item = toReplace[i];
    try {
      const byteCharacters = atob(item.base64Data);
      const byteNumbers = new Uint8Array(byteCharacters.length);
      for (let j = 0; j < byteCharacters.length; j++) {
        byteNumbers[j] = byteCharacters.charCodeAt(j);
      }
      const ext = item.mimeType === 'jpeg' ? 'jpg' : item.mimeType;
      const file = new File([byteNumbers], `pasted_image_${Date.now()}_${i}.${ext}`, { type: `image/${item.mimeType}` });

      const uploadedUrls = await uploadFiles([file]);
      if (uploadedUrls && uploadedUrls.length > 0) {
        const fullUrl = API_BASE.replace('/api', '') + uploadedUrls[0];
        updatedContent = updatedContent.split(item.dataUrl).join(fullUrl);
      }
    } catch (err) {
      console.error('Failed to auto-upload base64 image:', err);
    }
  }

  return updatedContent;
}

export default function NewsPage() {
  const user = getAuthUser();
  const isAdmin = user?.role === 'ADMIN';
  const cachedData = getApiCacheSync<{ posts: BackendPost[] }>('/posts');
  const [posts, setPosts] = useState<BackendPost[]>(() => (cachedData?.posts || []).filter(isArticlePost));
  const [loading, setLoading] = useState(!cachedData);

  registerQuillFormats();

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyPost);
  const [editingId, setEditingId] = useState('');
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [slugEdited, setSlugEdited] = useState(false);
  
  const quillRef = useRef<ReactQuill>(null);

  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files ? input.files[0] : null;
      if (!file) return;

      try {
        const uploadedUrls = await uploadFiles([file]);
        if (uploadedUrls && uploadedUrls.length > 0) {
          const quill = quillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection(true);
            const imageUrl = API_BASE.replace('/api', '') + uploadedUrls[0];
            quill.insertEmbed(range.index, 'image', imageUrl);
            quill.setSelection(range.index + 1, 0);
          }
        }
      } catch (err) {
        console.error('Upload failed', err);
      }
    };
  };

  const uploadCoverImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setStatus('Đang tải ảnh lên...');
    try {
      const uploadedUrls = await uploadFiles([e.target.files[0]]);
      if (uploadedUrls && uploadedUrls.length > 0) {
        setForm({ ...form, coverImage: API_BASE.replace('/api', '') + uploadedUrls[0] });
      }
      setStatus('');
    } catch (error) {
      setStatus('Lỗi tải ảnh đại diện.');
    }
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ font: [] }, { size: FONT_SIZES }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: QUILL_COLORS }, { background: QUILL_COLORS }],
        [{ script: 'sub'}, { script: 'super' }],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false
    },
    imageFormatter: {}
  }), []);

  const formats = [
    'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'video',
    'color', 'background',
    'align', 'code-block', 'script',
    'width', 'height', 'style', 'data-align', 'alt'
  ];

  async function loadPosts() {
    setLoading(true);
    const response = await tryApiRequest<{ posts: BackendPost[] }>('/posts', { useCache: true });
    setPosts((response?.posts ?? []).filter(isArticlePost));
    setLoading(false);
  }

  useEffect(() => {
    loadPosts();
  }, []);

  useEffect(() => {
    if (!showModal) return;

    const timer = setTimeout(() => {
      const quill = quillRef.current?.getEditor();
      if (!quill || !quill.root) return;

      const handlePaste = async (e: ClipboardEvent) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.type.indexOf('image') !== -1) {
            e.preventDefault();
            const file = item.getAsFile();
            if (!file) continue;
            setStatus('Đang tải ảnh dán lên máy chủ...');
            try {
              const urls = await uploadFiles([file]);
              if (urls && urls.length > 0) {
                const range = quill.getSelection(true) || { index: quill.getLength() };
                const fullUrl = API_BASE.replace('/api', '') + urls[0];
                quill.insertEmbed(range.index, 'image', fullUrl);
                quill.setSelection(range.index + 1, 0);
                setStatus('');
              }
            } catch (err) {
              setStatus('Lỗi tải ảnh dán lên máy chủ.');
            }
            return;
          }
        }
      };

      const handleDrop = async (e: DragEvent) => {
        const files = e.dataTransfer?.files;
        if (files && files.length > 0 && files[0].type.startsWith('image/')) {
          e.preventDefault();
          const file = files[0];
          setStatus('Đang tải ảnh kéo thả lên máy chủ...');
          try {
            const urls = await uploadFiles([file]);
            if (urls && urls.length > 0) {
              const range = quill.getSelection(true) || { index: quill.getLength() };
              const fullUrl = API_BASE.replace('/api', '') + urls[0];
              quill.insertEmbed(range.index, 'image', fullUrl);
              quill.setSelection(range.index + 1, 0);
              setStatus('');
            }
          } catch (err) {
            setStatus('Lỗi tải ảnh kéo thả lên máy chủ.');
          }
        }
      };

      quill.root.addEventListener('paste', handlePaste);
      quill.root.addEventListener('drop', handleDrop);

      return () => {
        quill.root.removeEventListener('paste', handlePaste);
        quill.root.removeEventListener('drop', handleDrop);
      };
    }, 150);

    return () => clearTimeout(timer);
  }, [showModal]);

  async function submitPost(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setStatus('Đang kiểm tra và xử lý nội dung...');

    try {
      const processedContent = await processBase64Images(form.content, (msg) => setStatus(msg));
      setStatus('Đang lưu bài viết...');
      const payload = {
        ...form,
        content: normalizeEditorContent(processedContent),
        slug: form.slug || makeSlug(form.title),
        publishedAt: form.publishedAt ? new Date(form.publishedAt).toISOString() : undefined,
      };

      if (editingId) {
        const res = await apiRequest<{ post: BackendPost }>(`/posts/${editingId}`, { method: 'PUT', body: JSON.stringify(payload) }, true);
        setPosts(prev => prev.map(p => p.id === editingId ? res.post : p).filter(isArticlePost));
      } else {
        const res = await apiRequest<{ post: BackendPost }>('/posts', { method: 'POST', body: JSON.stringify(payload) }, true);
        if (isArticlePost(res.post)) {
          setPosts(prev => [res.post, ...prev]);
        }
      }
      setShowModal(false);
      setEditingId('');
      setForm(emptyPost);
      setStatus('');
      invalidateApiCache('/posts');
      // Optimistic update: state already updated from server response, no reload needed
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Không thể lưu bài viết');
    } finally {
      setSubmitting(false);
    }
  }

  function confirmDelete(id: string) {
    setDeleteId(id);
  }

  async function executeDelete() {
    if (!deleteId) return;
    const targetId = deleteId;
    setDeleteId(null);
    setPosts(prev => prev.filter(p => p.id !== targetId));
    try {
      await apiRequest(`/posts/${targetId}`, { method: 'DELETE' }, true);
      invalidateApiCache('/posts');
      // Optimistic update: item already removed from state, no re-fetch needed
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Không thể xóa');
      await loadPosts(); // Rollback on error
    }
  }

  function openAdd() {
    setEditingId('');
    setForm(emptyPost);
    setSlugEdited(false);
    setStatus('');
    setShowModal(true);
  }

  async function openEdit(post: BackendPost) {
    setEditingId(post.id);
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: normalizeEditorContent(post.content || ''),
      category: post.category,
      coverImage: post.coverImage || '',
      publishedAt: post.publishedAt ? post.publishedAt.slice(0, 16) : '',
    });
    setSlugEdited(true); // editing an existing post shouldn't auto-update slug unless reset
    setStatus('');
    setShowModal(true);

    if (!post.content) {
      setStatus('Đang tải nội dung bài viết...');
      try {
        const res = await apiRequest<{ post: BackendPost }>(`/posts/${post.id}`);
        if (res?.post) {
          setForm(prev => ({
            ...prev,
            content: normalizeEditorContent(res.post.content || ''),
          }));
          setStatus('');
        }
      } catch (err) {
        setStatus('Không thể tải toàn bộ nội dung bài viết.');
      }
    }
  }


  return (
    <div className="page-block">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.5 }}>
        <EditablePageHeader pageKey="news" defaultEyebrow="Tin tức & Hỗ trợ" defaultTitle="Bài viết, hướng dẫn và hỗ trợ kỹ thuật" />
      </motion.div>

      {loading && posts.length === 0 ? (
        <div style={{ minHeight: '100vh' }} />
      ) : (posts.length > 0 || isAdmin) ? (
        <div className="news-list" style={{ marginTop: '32px' }}>
          {isAdmin && (
            <motion.article
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.15 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="news-card glass-card"
              onClick={openAdd}
              style={{
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '380px',
                border: '2px dashed rgba(255, 255, 255, 0.18)',
                background: 'rgba(255, 255, 255, 0.02)',
                boxShadow: 'none',
                transition: 'all 0.25s ease',
                padding: '24px'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.05)'; e.currentTarget.style.borderColor = 'var(--neon-red)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.18)'; }}
            >
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: 'rgba(239, 68, 68, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '32px', color: 'var(--neon-red)', marginBottom: '16px',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                boxShadow: '0 0 15px rgba(239, 68, 68, 0.2)'
              }}>
                +
              </div>
              <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#f8fafc', fontWeight: 700 }}>Viết bài mới</h3>
            </motion.article>
          )}
          {posts.map((article, idx) => (
            <motion.article
              key={article.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.15 }}
              transition={{ duration: 0.5, delay: idx * 0.08, ease: 'easeOut' }}
              className="news-card glass-card"
              style={{ position: 'relative' }}
            >
              {article.coverImage && (
                <div style={{ height: '200px', width: '100%', overflow: 'hidden' }}>
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  />
                </div>
              )}
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <span className="tag" style={{ marginBottom: '10px' }}>{article.category}</span>
                <Link to={`/news/${article.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    color: '#f8fafc',
                    margin: '0 0 12px 0',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {article.title}
                  </h3>
                </Link>
                {article.excerpt && (
                  <p style={{
                    fontSize: '0.95rem',
                    color: '#94a3b8',
                    lineHeight: 1.6,
                    marginBottom: '16px',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {article.excerpt}
                  </p>
                )}
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 'auto', marginBottom: '14px' }}>
                  {new Date(article.publishedAt || article.createdAt || Date.now()).toLocaleDateString('vi-VN')}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <Link to={`/news/${article.slug}`} className="text-link" style={{ textDecoration: 'none', fontSize: '0.95rem' }}>
                    Đọc tiếp →
                  </Link>
                  {isAdmin && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="button secondary small glow-border" style={{ padding: '4px 12px', borderRadius: '8px', fontSize: '13px' }} onClick={() => openEdit(article)}>
                        Sửa
                      </button>
                      <button style={{ padding: '4px 12px', borderRadius: '8px', fontSize: '13px', background: 'rgba(239, 68, 68, 0.15)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)', cursor: 'pointer', fontWeight: 600 }} onClick={() => confirmDelete(article.id)}>
                        Xóa
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      ) : (
        <div className="glass-card" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', marginTop: '32px' }}>
          <p>Chưa có bài viết nào.</p>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div 
            className="modal-content" 
            onClick={e => e.stopPropagation()}
            onScroll={() => window.dispatchEvent(new Event('resize'))}
          >
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2 style={{ borderBottom: '1px solid var(--glass-border)', paddingBottom: '16px', color: '#f8fafc', fontSize: '20px', fontWeight: 700, marginBottom: '20px' }}>{editingId ? 'Sửa bài viết' : 'Thêm bài viết'}</h2>
            <form className="admin-form" onSubmit={submitPost}>
              {status && <p className="auth-status" style={{ color: 'red' }}>{status}</p>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="field-label">Tiêu đề</label>
                  <input className="input-box" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: slugEdited ? form.slug : makeSlug(e.target.value) })} required placeholder="VD: Hướng dẫn cập nhật Firmware BMCU" />
                </div>
                <div>
                  <label className="field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Đường dẫn tĩnh (Slug)</span>
                    <button type="button" className="reset-slug-btn" onClick={() => { setSlugEdited(false); setForm({ ...form, slug: makeSlug(form.title) }); }} title="Tạo lại từ Tiêu đề">⟲ Auto</button>
                  </label>
                  <input className="input-box" value={form.slug} onChange={(e) => { setSlugEdited(true); setForm({ ...form, slug: e.target.value }); }} required placeholder="vd: huong-dan-cap-nhat-firmware" />
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="field-label">Danh mục</label>
                  <input className="input-box" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required placeholder="VD: Hướng dẫn, Tin tức, Sự kiện..." />
                </div>
                <div>
                  <label className="field-label">Ngày publish (Tùy chọn)</label>
                  <input className="input-box" type="datetime-local" value={form.publishedAt} onChange={(e) => setForm({ ...form, publishedAt: e.target.value })} />
                </div>
              </div>

              <label className="field-label">Ảnh đại diện (Cover Image)</label>
              <div 
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.onchange = (e: any) => uploadCoverImage(e);
                  input.click();
                }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  border: '2px dashed rgba(255, 255, 255, 0.18)', borderRadius: '12px', padding: '24px', cursor: 'pointer',
                  background: 'rgba(255, 255, 255, 0.03)', transition: 'all 0.2s', minHeight: '180px',
                  position: 'relative', overflow: 'hidden', marginBottom: '8px'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--neon-red)'; e.currentTarget.style.background = 'rgba(225, 29, 72, 0.05)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.18)'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)'; }}
              >
                {form.coverImage ? (
                  <>
                    <img src={form.coverImage} alt="Cover" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.9 }} />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '16px' }}>
                       <span style={{ color: 'white', fontWeight: 600, fontSize: '14px', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>Nhấn để thay đổi ảnh</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '32px', color: '#94a3b8', marginBottom: '8px' }}>📸</div>
                    <div style={{ color: '#475569', fontWeight: 500 }}>Nhấn để tải lên ảnh bìa</div>
                    <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '4px' }}>Khuyên dùng ảnh tỷ lệ 16:9 (VD: 1200x675)</div>
                  </>
                )}
              </div>
              
              <label className="field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Tóm tắt (Tối đa 150 ký tự)</span>
                <span style={{ fontSize: '0.85rem', color: (form.excerpt?.length || 0) >= 150 ? 'red' : '#888' }}>
                  {form.excerpt?.length || 0}/150
                </span>
              </label>
              <input className="input-box" value={form.excerpt} maxLength={150} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} required placeholder="Nhập một đoạn mô tả ngắn gọn về bài viết (sẽ hiển thị ở trang danh sách bài viết)..." />
              
              <label className="field-label">Nội dung</label>
              <ReactQuill 
                ref={quillRef}
                theme="snow" 
                value={form.content} 
                onChange={(val) => setForm({ ...form, content: val })} 
                modules={modules}
                formats={formats}
                bounds=".modal-content"
                scrollingContainer=".modal-content"
                style={{ minHeight: '300px', marginBottom: '50px' }}
              />
              
              <div className="modal-actions">
                <button className="button outline" type="button" onClick={() => setShowModal(false)}>Hủy bỏ</button>
                <button className="button primary" type="submit" disabled={submitting} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  {submitting && <span className="button-spinner" />}
                  {submitting ? (editingId ? 'Đang cập nhật...' : 'Đang lưu...') : (editingId ? 'Cập nhật bài viết' : 'Lưu bài viết')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteId !== null}
        message="Bạn có chắc chắn muốn xóa bài viết này không? Hành động này không thể hoàn tác."
        onCancel={() => setDeleteId(null)}
        onConfirm={executeDelete}
      />
    </div>
  );
}
